import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getCountries, 
  getCountryCallingCode, 
  AsYouType, 
  getExampleNumber 
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';

// ИСПРАВЛЕННЫЙ ИМПОРТ: Импортируем всё как объект
import * as FlagIcons from 'country-flag-icons/react/3x2';

import { 
  ChevronDownIcon, 
  MagnifyingGlassIcon, 
  CheckIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';

const ALL_COUNTRIES = getCountries().map(iso => {
  try {
    return {
      iso,
      code: `+${getCountryCallingCode(iso)}`,
      // Используем английские названия для стабильности
      name: new Intl.DisplayNames(['en'], { type: 'region' }).of(iso)
    };
  } catch (e) { return null; }
}).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));

const PhoneAuth = ({ onSuccess, labelClassName, buttonClassName }) => {
  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('+7');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState({ iso: 'RU', code: '+7', name: 'Russia' });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef(null);

  // ИСПРАВЛЕННЫЙ КОМПОНЕНТ ФЛАГА
  const FlagComponent = ({ iso, className }) => {
    // Проверяем наличие флага в объекте импорта
    const Flag = FlagIcons[iso];
    if (!Flag) return <div className={`${className} bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-500`}>{iso}</div>;
    return <Flag className={className} />;
  };

  const filteredCountries = useMemo(() => {
    return ALL_COUNTRIES.filter(c => 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      c.code.includes(searchQuery)
    );
  }, [searchQuery]);

  const placeholderExample = useMemo(() => {
    try {
      const example = getExampleNumber(selectedCountry.iso, metadata);
      return example ? example.formatInternational() : 'Enter number';
    } catch (e) { return 'Enter phone number'; }
  }, [selectedCountry.iso]);

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/[^\d]/g, '');
    } else {
      value = '+' + value.slice(1).replace(/[^\d]/g, '');
    }

    const asYouType = new AsYouType();
    asYouType.input(value);
    const detectedCountry = asYouType.getCountry();

    if (detectedCountry && detectedCountry !== selectedCountry.iso) {
      const newCountry = ALL_COUNTRIES.find(c => c.iso === detectedCountry);
      if (newCountry) setSelectedCountry(newCountry);
    }
    setPhoneNumber(value);
  };

  return (
    <div className="space-y-4 relative text-left">
      <AnimatePresence mode="wait">
        {step === 'phone' ? (
          <motion.div key="phone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {labelClassName && <label className={labelClassName}>Phone Number</label>}
            
            <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-[22px] px-5 focus-within:bg-white focus-within:border-[#F65814] focus-within:ring-4 focus-within:ring-[#F65814]/5 transition-all duration-300 mt-2">
              
              <button 
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 hover:opacity-70 transition-opacity shrink-0 py-4 select-none outline-none group"
              >
                <div className="w-6 h-4 overflow-hidden rounded-sm shadow-sm border border-slate-100 flex-shrink-0">
                  <FlagComponent iso={selectedCountry.iso} className="w-full h-full object-cover" />
                </div>
                <ChevronDownIcon className="h-3 w-3 text-slate-400 group-hover:text-[#F65814] transition-colors" />
              </button>

              <input 
                ref={inputRef}
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                className="w-full bg-transparent ml-3 py-4 text-[15px] font-bold text-slate-900 placeholder:text-slate-300 tracking-tight outline-none appearance-none border-none focus:ring-0 caret-[#F65814]"
                style={{ WebkitAppearance: 'none', WebkitTapHighlightColor: 'transparent' }}
                placeholder={placeholderExample}
              />

              <AnimatePresence>
                {isDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute top-[calc(100%+10px)] left-0 w-full max-w-[280px] bg-white border border-slate-100 rounded-[24px] shadow-[0_25px_60px_rgba(0,0,0,0.12)] z-50 overflow-hidden"
                    >
                      <div className="p-4 border-b border-slate-50 flex items-center gap-2 text-slate-400 focus-within:text-[#F65814]">
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <input 
                          autoFocus
                          placeholder="Search country..."
                          className="w-full text-[13px] outline-none font-bold text-slate-800 bg-transparent appearance-none"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      
                      <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                        {filteredCountries.map((c) => (
                          <button
                            key={c.iso}
                            type="button"
                            onClick={() => {
                              setSelectedCountry(c);
                              setPhoneNumber(c.code);
                              setIsDropdownOpen(false);
                              inputRef.current?.focus();
                            }}
                            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors text-left outline-none group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-4 overflow-hidden rounded-sm border border-slate-100 shadow-sm flex-shrink-0">
                                <FlagComponent iso={c.iso} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[12px] font-black text-slate-800 uppercase tracking-tight group-hover:text-[#F65814] transition-colors">{c.name}</span>
                                <span className="text-[10px] font-bold text-slate-400">{c.code}</span>
                              </div>
                            </div>
                            {selectedCountry.iso === c.iso && <CheckIcon className="h-4 w-4 text-[#F65814]" />}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <button type="button" onClick={() => setStep('code')} className={`${buttonClassName} mt-8 w-full`}>
              Get Secure Code
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </motion.div>
        ) : (
          <motion.div key="code" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
             <button onClick={() => setStep('phone')} className="text-[10px] uppercase font-black text-[#F65814] tracking-[1.5px] mt-4 hover:underline outline-none">
               ← Change number
             </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhoneAuth;